import React, { useState } from 'react';
import { DateTime } from 'luxon';

interface Service {
  unique_identifier: string;
  public_data: {
    deceased_person: {
      full_name: string;
      avatar_url?: string;
    };
    event_date?: string;
  };
  last_signature_at?: string;
  inserted_at?: string;
}

interface ServiceSelectorProps {
  services: Service[];
  selectedServiceUid: string;
  onSelectService: (serviceUid: string) => void;
  onCustomServiceSelected?: (serviceUid: string) => void;
  onCustomServiceRemoved?: () => void;
  customSelectedService?: string | null;
  showNotConfiguredOption?: boolean;
  readOnlyMode?: boolean;
  onChangeRedirect?: () => void;
}

export function ServiceSelector({
  services,
  selectedServiceUid,
  onSelectService,
  onCustomServiceSelected,
  onCustomServiceRemoved,
  customSelectedService,
  showNotConfiguredOption = true,
  readOnlyMode = false,
  onChangeRedirect,
}: ServiceSelectorProps) {
  const [showAllServices, setShowAllServices] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedService = services.find(s => s.unique_identifier === selectedServiceUid);

  // Helper to format relative time
  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return null;
    const date = DateTime.fromISO(dateString);
    return date.toRelative();
  };

  // Check if a service is in the top 3
  const isInTop3 = (serviceUid: string) => {
    return services.slice(0, 3).some(s => s.unique_identifier === serviceUid);
  };

  // Get visible services - if custom selection, show it; otherwise show top 3
  const getVisibleServices = () => {
    if (customSelectedService && selectedServiceUid === customSelectedService) {
      const customService = services.find(s => s.unique_identifier === customSelectedService);
      return customService ? [customService] : services.slice(0, 3);
    }
    return services.slice(0, 3);
  };

  // Read-only mode: show selected service with option to change
  if (readOnlyMode && selectedServiceUid) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3 p-3 border-2 border-primary bg-primary/5 rounded">
          {selectedService?.public_data.deceased_person?.avatar_url && (
            <img
              src={selectedService.public_data.deceased_person.avatar_url}
              alt=""
              className="w-10 h-10 rounded-full object-cover"
            />
          )}
          {selectedService && !selectedService.public_data.deceased_person?.avatar_url && (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <i className="ki-filled ki-profile-circle text-gray-400 text-2xl"></i>
            </div>
          )}
          <div className="flex-1">
            <div className="font-medium text-sm">
              {selectedService?.public_data.deceased_person?.full_name || 'Service'}
            </div>
            <div className="text-xs text-gray-500 space-y-0.5">
              {selectedService?.inserted_at && (
                <div>Created {formatRelativeTime(selectedService.inserted_at)}</div>
              )}
              {selectedService?.last_signature_at && (
                <div>Last signature {formatRelativeTime(selectedService.last_signature_at)}</div>
              )}
            </div>
          </div>
          <i className="ki-filled ki-check-circle text-success text-xl"></i>
        </div>
        {onChangeRedirect && (
          <button
            type="button"
            className="btn btn-light btn-sm w-full"
            onClick={onChangeRedirect}
          >
            Change Redirect
          </button>
        )}
      </div>
    );
  }

  // Read-only mode: show not configured with option to configure
  if (readOnlyMode && selectedServiceUid === '') {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3 p-3 border-2 border-warning bg-warning/5 rounded">
          <i className="ki-filled ki-information-2 text-warning text-xl"></i>
          <div>
            <div className="font-medium text-sm">Not configured</div>
            <div className="text-xs text-gray-500">This QR code hasn't been set up yet</div>
          </div>
        </div>
        {onChangeRedirect && (
          <button
            type="button"
            className="btn btn-primary btn-sm w-full"
            onClick={onChangeRedirect}
          >
            Configure Redirect
          </button>
        )}
      </div>
    );
  }

  // Edit mode: show all options
  return (
    <div className="space-y-2">
      {/* Not configured option */}
      {showNotConfiguredOption && (
        <div
          className={`flex items-center gap-3 p-3 border-2 rounded cursor-pointer hover:bg-gray-50 ${
            selectedServiceUid === '' && !showAllServices ? 'border-primary bg-primary/5' : 'border-gray-300'
          }`}
          onClick={() => {
            onSelectService('');
            setShowAllServices(false);
          }}
        >
          <div>
            <div className="font-medium text-sm">Not configured</div>
            <div className="text-xs text-gray-500">Show "not configured" page</div>
          </div>
        </div>
      )}

      {/* Recent services */}
      {getVisibleServices().map((service) => {
        const isCustom = customSelectedService === service.unique_identifier;
        return (
          <div
            key={service.unique_identifier}
            className={`flex items-center gap-3 p-3 border-2 rounded ${
              selectedServiceUid === service.unique_identifier ? 'border-primary bg-primary/5' : 'border-gray-300'
            } ${isCustom ? '' : 'cursor-pointer hover:bg-gray-50'}`}
            onClick={() => {
              if (!isCustom) {
                onSelectService(service.unique_identifier);
                setShowAllServices(false);
              }
            }}
          >
            {service.public_data.deceased_person?.avatar_url && (
              <img
                src={service.public_data.deceased_person.avatar_url}
                alt=""
                className="w-10 h-10 rounded-full object-cover"
              />
            )}
            {!service.public_data.deceased_person?.avatar_url && (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <i className="ki-filled ki-profile-circle text-gray-400 text-2xl"></i>
              </div>
            )}
            <div className="flex-1">
              <div className="font-medium text-sm">
                {service.public_data.deceased_person?.full_name || 'Unnamed Service'}
              </div>
              <div className="text-xs text-gray-500 space-y-0.5">
                {service.inserted_at && (
                  <div>Created {formatRelativeTime(service.inserted_at)}</div>
                )}
                {service.last_signature_at && (
                  <div>Last signature {formatRelativeTime(service.last_signature_at)}</div>
                )}
              </div>
            </div>
            {isCustom && onCustomServiceRemoved && (
              <button
                type="button"
                className="text-error hover:bg-error/10 p-1 rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  onCustomServiceRemoved();
                }}
              >
                <i className="ki-filled ki-cross-circle text-xl"></i>
              </button>
            )}
          </div>
        );
      })}

      {/* Find another service option */}
      <div
        className={`flex items-center gap-3 p-3 border-2 rounded cursor-pointer hover:bg-gray-50 ${
          showAllServices ? 'border-primary bg-primary/5' : 'border-gray-300'
        }`}
        onClick={() => {
          setShowAllServices(true);
          setSearchQuery('');
        }}
      >
        <div>
          <div className="font-medium text-sm">Find another service</div>
          <div className="text-xs text-gray-500">Search all services</div>
        </div>
      </div>

      {/* Search all services */}
      {showAllServices && (
        <div className="pl-9 space-y-2">
          <input
            type="text"
            className="input input-sm"
            placeholder="Search services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          {searchQuery && (
            <div className="max-h-60 overflow-y-auto space-y-2">
              {services
                .filter((service) => {
                  const query = searchQuery.toLowerCase();
                  return service.public_data.deceased_person?.full_name?.toLowerCase().includes(query);
                })
                .map((service) => (
                  <div
                    key={service.unique_identifier}
                    className={`flex items-center gap-3 p-2 border-2 rounded cursor-pointer hover:bg-gray-50 ${
                      selectedServiceUid === service.unique_identifier ? 'border-primary bg-primary/5' : 'border-gray-300'
                    }`}
                    onClick={() => {
                      onSelectService(service.unique_identifier);
                      // If this service is not in the top 3, mark it as custom
                      if (!isInTop3(service.unique_identifier) && onCustomServiceSelected) {
                        onCustomServiceSelected(service.unique_identifier);
                      }
                      setShowAllServices(false);
                      setSearchQuery('');
                    }}
                  >
                    {service.public_data.deceased_person?.avatar_url && (
                      <img
                        src={service.public_data.deceased_person.avatar_url}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    )}
                    {!service.public_data.deceased_person?.avatar_url && (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <i className="ki-filled ki-profile-circle text-gray-400 text-xl"></i>
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-sm">
                        {service.public_data.deceased_person?.full_name || 'Unnamed Service'}
                      </div>
                      <div className="text-xs text-gray-500 space-y-0.5">
                        {service.inserted_at && (
                          <div>Created {formatRelativeTime(service.inserted_at)}</div>
                        )}
                        {service.last_signature_at && (
                          <div>Last signature {formatRelativeTime(service.last_signature_at)}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
